// src/components/CalendarView.jsx
import { useState, useEffect, useCallback } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { calendarService } from '../services/calendarService';
import { supabase } from '../lib/supabase';
import { WeekView, MonthView } from './CalendarGrid';
import EventDetailPanel from './EventDetailPanel';
import SlotActionMenu from './SlotActionMenu';
import EventContextMenu from './EventContextMenu';
import styles from './CalendarView.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

// Design-token colours for event types
const EVENT_COLOURS = {
  availability: '#4a7c59', // --color-success (green)
  booking:      '#2f5a5a', // --color-booking (deep teal)
  proposal:     '#c8843a', // --color-amber (amber)
};

function toEvents(availabilityBlocks, bookings, proposals) {
  const events = [];

  for (const block of availabilityBlocks) {
    events.push({
      id: `avail-${block.id}`,
      _rawId: block.id,
      start: block.start_time,
      end: block.end_time,
      type: 'availability',
      label: block.provider ? `Busy (${block.provider})` : 'Busy',
      colour: EVENT_COLOURS.availability,
      provider: block.provider || null,
    });
  }

  for (const booking of bookings) {
    events.push({
      id: `booking-${booking.id}`,
      _rawId: booking.id,
      start: booking.start_time,
      end: booking.end_time,
      type: 'booking',
      label: booking.customer_name || 'Booking',
      colour: EVENT_COLOURS.booking,
      status: booking.status,
      customerName: booking.customer_name,
      customerEmail: booking.customer_email,
      notes: booking.notes,
      vennLinkId: booking.venn_link?.id,
    });
  }

  for (const proposal of proposals) {
    for (const slot of proposal.slots || []) {
      events.push({
        id: `proposal-${proposal.id}-${slot.id}`,
        _rawId: proposal.id,
        start: slot.start_time,
        end: slot.end_time,
        type: 'proposal',
        label: 'Proposal',
        colour: EVENT_COLOURS.proposal,
        status: proposal.status,
        proposerName: proposal.proposer_name || proposal.customer_name,
        message: proposal.message,
      });
    }
  }

  return events;
}

async function fetchBookings(start, end) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return [];
  const params = new URLSearchParams({
    from: start.toISOString(),
    to: end.toISOString(),
    limit: '200',
  });
  const res = await fetch(`${API_URL}/v1/bookings?${params}`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!res.ok) return [];
  const json = await res.json();
  return json.bookings || [];
}

async function fetchProposals() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return [];
  const res = await fetch(`${API_URL}/v1/proposals?status=proposed&limit=100`, {
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!res.ok) return [];
  const json = await res.json();
  return json.proposals || [];
}

const CalendarView = ({ userId, refreshTrigger = 0 }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('week');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [internalRefresh, setInternalRefresh] = useState(0);

  // Slot-click popover state
  const [slotMenu, setSlotMenu] = useState(null); // { slot: Date, anchor: {x, y} }

  // Event right-click context menu state
  const [contextMenu, setContextMenu] = useState(null); // { event, anchor: {x, y} }

  const refresh = useCallback(() => setInternalRefresh(n => n + 1), []);

  useEffect(() => {
    if (!userId) return;

    const fetchAllData = async () => {
      setLoading(true);
      try {
        // Compute date range for the current view window
        const start = new Date(currentDate);
        const end = new Date(currentDate);
        if (view === 'week') {
          const dow = start.getDay();
          start.setDate(start.getDate() - ((dow + 6) % 7)); // Monday
          end.setDate(start.getDate() + 6);
        } else {
          start.setDate(1);
          end.setMonth(end.getMonth() + 1, 0); // last day of month
        }
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        const [availResult, bookingsResult, proposalsResult] = await Promise.allSettled([
          calendarService.getAvailabilityBlocks(userId, start, end),
          fetchBookings(start, end),
          fetchProposals(),
        ]);

        const blocks    = availResult.status     === 'fulfilled' ? availResult.value     : [];
        const bookings  = bookingsResult.status  === 'fulfilled' ? bookingsResult.value  : [];
        const proposals = proposalsResult.status === 'fulfilled' ? proposalsResult.value : [];

        setEvents(toEvents(blocks, bookings, proposals));
      } catch (error) {
        console.error('CalendarView fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [userId, currentDate, view, refreshTrigger, internalRefresh]);

  // ── Slot click: show action popover ──────────────────────────────────────
  const handleSlotClick = useCallback((slot, mouseEvent) => {
    // mouseEvent may not always be provided (MonthView calls onSlotClick(date, null))
    const anchor = mouseEvent
      ? { x: mouseEvent.clientX, y: mouseEvent.clientY }
      : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    setSlotMenu({ slot, anchor });
  }, []);

  // WeekView passes Date only; we wrap to capture mouse position via the
  // onSlotClick prop signature — WeekView only passes the Date, so we use
  // a click-position-aware wrapper via the column click event.
  // Actually WeekView's onSlotClick only passes Date — we track mouse position
  // using a document mousemove snapshot instead.
  const lastMousePos = { x: 0, y: 0 };

  // Capture last mouse position for positioning the slot menu
  useEffect(() => {
    function trackMouse(e) {
      lastMousePos.x = e.clientX;
      lastMousePos.y = e.clientY;
    }
    document.addEventListener('mousemove', trackMouse);
    return () => document.removeEventListener('mousemove', trackMouse);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWeekSlotClick = useCallback((slot) => {
    setSlotMenu({ slot, anchor: { x: lastMousePos.x, y: lastMousePos.y } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Block time: create a 1-hour manual busy block ────────────────────────
  const handleBlockTime = useCallback(async (slot) => {
    const start = new Date(slot);
    const end = new Date(slot);
    end.setHours(end.getHours() + 1);

    const { error } = await supabase.from('availability_blocks').insert({
      user_id: userId,
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      provider: null, // manual block
    });

    if (error) {
      console.error('Failed to block time:', error);
    } else {
      refresh();
    }
  }, [userId, refresh]);

  // ── Create availability window: 2-hour open availability slot ────────────
  const handleCreateWindow = useCallback(async (slot) => {
    // Reuse the block mechanism — provider=null means manually created.
    // "Block" means busy; for an availability window we create a 2-hour block.
    const start = new Date(slot);
    const end = new Date(slot);
    end.setHours(end.getHours() + 2);

    const { error } = await supabase.from('availability_blocks').insert({
      user_id: userId,
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      provider: null,
    });

    if (error) {
      console.error('Failed to create availability window:', error);
    } else {
      refresh();
    }
  }, [userId, refresh]);

  // ── Drag select: create a block spanning the dragged range ───────────────
  const handleDragSelect = useCallback(async (startDt, endDt) => {
    const { error } = await supabase.from('availability_blocks').insert({
      user_id: userId,
      start_time: startDt.toISOString(),
      end_time: endDt.toISOString(),
      provider: null,
    });

    if (error) {
      console.error('Failed to create drag-selected block:', error);
    } else {
      refresh();
    }
  }, [userId, refresh]);

  // ── Event left-click: open detail panel ─────────────────────────────────
  const handleEventClick = useCallback((event) => {
    setSelectedEvent(event);
  }, []);

  // ── Event right-click: show context menu ─────────────────────────────────
  const handleEventContextMenu = useCallback((event, anchor) => {
    setContextMenu({ event, anchor });
  }, []);

  // ── Cancel booking via API ────────────────────────────────────────────────
  const handleCancelBooking = useCallback(async (event) => {
    setActionLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_URL}/v1/bookings/${event._rawId}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        setSelectedEvent(null);
        refresh();
      }
    } catch (err) {
      console.error('Failed to cancel booking:', err);
    } finally {
      setActionLoading(false);
    }
  }, [refresh]);

  // ── Remove manual availability block ─────────────────────────────────────
  const handleRemoveBlock = useCallback(async (event) => {
    const { error } = await supabase
      .from('availability_blocks')
      .delete()
      .eq('id', event._rawId);

    if (error) {
      console.error('Failed to remove block:', error);
    } else {
      setSelectedEvent(null);
      refresh();
    }
  }, [refresh]);

  // Unified cancel handler — routes by event type
  const handleCancelEvent = useCallback((event) => {
    if (event.type === 'booking') return handleCancelBooking(event);
    if (event.type === 'availability') return handleRemoveBlock(event);
  }, [handleCancelBooking, handleRemoveBlock]);

  // ── Accept / Decline proposal ─────────────────────────────────────────────
  const handleAcceptProposal = useCallback(async (event) => {
    setActionLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_URL}/v1/proposals/${event._rawId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        setSelectedEvent(null);
        refresh();
      }
    } catch (err) {
      console.error('Failed to accept proposal:', err);
    } finally {
      setActionLoading(false);
    }
  }, [refresh]);

  const handleDeclineProposal = useCallback(async (event) => {
    setActionLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(`${API_URL}/v1/proposals/${event._rawId}/decline`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        setSelectedEvent(null);
        refresh();
      }
    } catch (err) {
      console.error('Failed to decline proposal:', err);
    } finally {
      setActionLoading(false);
    }
  }, [refresh]);

  if (loading) {
    return (
      <div className={styles.card}>
        <div className={styles.loadingWrap}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <CalendarIcon className={styles.headerIcon} size={24} />
          <h2 className={styles.headerTitle}>My Calendar</h2>
        </div>
        <div className={styles.viewToggle}>
          <button
            onClick={() => setView('week')}
            className={`${styles.viewBtn}${view === 'week' ? ` ${styles.viewBtnActive}` : ''}`}
          >
            Week
          </button>
          <button
            onClick={() => setView('month')}
            className={`${styles.viewBtn}${view === 'month' ? ` ${styles.viewBtnActive}` : ''}`}
          >
            Month
          </button>
        </div>
      </div>

      {view === 'week' ? (
        <WeekView
          currentDate={currentDate}
          events={events}
          onDateChange={setCurrentDate}
          onSlotClick={handleWeekSlotClick}
          onEventClick={handleEventClick}
          onEventContextMenu={handleEventContextMenu}
          onDragSelect={handleDragSelect}
        />
      ) : (
        <MonthView
          currentDate={currentDate}
          events={events}
          onDateChange={setCurrentDate}
          onSlotClick={(slot) => handleSlotClick(slot, null)}
          onEventClick={handleEventClick}
        />
      )}

      <div className={styles.summary}>
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <div className={styles.legendDot} style={{ background: EVENT_COLOURS.availability }} />
            <span>Availability</span>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendDot} style={{ background: EVENT_COLOURS.booking }} />
            <span>Booking</span>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendDot} style={{ background: EVENT_COLOURS.proposal }} />
            <span>Proposal</span>
          </div>
        </div>
      </div>

      {/* Slot action popover (click on empty time slot) */}
      {slotMenu && (
        <SlotActionMenu
          slot={slotMenu.slot}
          anchor={slotMenu.anchor}
          onBlock={handleBlockTime}
          onWindow={handleCreateWindow}
          onClose={() => setSlotMenu(null)}
        />
      )}

      {/* Event right-click context menu */}
      {contextMenu && (
        <EventContextMenu
          event={contextMenu.event}
          anchor={contextMenu.anchor}
          onDetails={(event) => { setSelectedEvent(event); setContextMenu(null); }}
          onCancel={handleCancelEvent}
          onAccept={contextMenu.event?.type === 'proposal' ? handleAcceptProposal : undefined}
          onDecline={contextMenu.event?.type === 'proposal' ? handleDeclineProposal : undefined}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Event detail panel (left-click or "Details" from context menu) */}
      {selectedEvent && (
        <EventDetailPanel
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onAccept={selectedEvent.type === 'proposal' ? handleAcceptProposal : undefined}
          onDecline={selectedEvent.type === 'proposal' ? handleDeclineProposal : undefined}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default CalendarView;
